import cn from "classnames";
import { styled } from "@linaria/react";
import React from "react";
import { easingBounce, easingBounceMs, fonts } from "..";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import { color } from "../../ui";
import Spinner from "../Spinner";
import TooltipV2 from "../TooltipV2";
import { InformationLine } from "../IconV2/react";
import { spacings } from "../Theme";
import { css } from "@linaria/core";

const SWITCH_DISABLED_OPACITY = 0.5;

const LEFT = "left";
const RIGHT = "right";
const LOADING = "loading";
const DISABLED = "disabled";
const SMALL = "small";

const SpinnerIcon = styled(Spinner)`
  height: 8px;
  display: block;
  margin: 2px auto;
  color: ${color.lightNitrogen500_Main};

  &.${SMALL} {
    height: 4px;
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  visibility: hidden;
  top: -2px;
`;

const SwitchWagon = styled.span`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transition:
    left ${easingBounceMs}ms ${easingBounce},
    background-color ${easingBounceMs}ms ${easingBounce};
  top: 2px;
  left: 2px;
  background-color: ${color.hydrogen};
  &.${LOADING} {
    left: 10px;
  }

  &.${SMALL} {
    height: 8px;
    width: 8px;

    &.${LOADING} {
      left: 8px;
    }
  }
`;

const SwitchRail = styled.a`
  flex: none;
  width: 32px;
  height: 16px;
  border-radius: 8px;
  position: relative;
  transition: background-color ${easingBounceMs}ms ${easingBounce};
  background-color: ${color.titanium400};
  margin-top: 2px;

  &.${SMALL} {
    height: 12px;
    width: 24px;
  }

  .justify & {
    &.${RIGHT} {
      margin-left: auto !important;
    }
  }

  ${/* sc-selector */ HiddenInput}:checked ~ &:not(.${LOADING}) {
    background-color: ${color.lightNitrogen500_Main};
    ${/* sc-selector */ SwitchWagon} {
      left: 18px;
    }

    &.${SMALL} ${/* sc-selector */ SwitchWagon} {
      left: 14px;
    }
  }
`;

const Wrapper = styled.label`
  color: ${color.carbon800};
  display: flex;
  font: ${fonts.body1};
  cursor: pointer;
  border: 1px solid transparent;
  align-items: center;

  &.${SMALL} {
    font: ${fonts.body2};
  }

  &.${LOADING}, &.${DISABLED} {
    cursor: not-allowed;
    opacity: ${SWITCH_DISABLED_OPACITY};
  }

  .${LEFT} {
    margin-right: 8px;
  }

  .${RIGHT} {
    margin-left: 8px;
  }

  &:focus {
    border-radius: 2px;
    outline: 1px solid ${color.lightNitrogen600};
    outline-offset: 0;
  }

  &:hover {
    ${/* sc-selector */ HiddenInput}:not(:checked):not(:disabled) ~${/* sc-selector */ SwitchRail} {
      background-color: ${color.titanium500};
    }
    ${/* sc-selector */ HiddenInput}:not(:disabled) ~${/* sc-selector */ SwitchRail} {
      background-color: ${color.lightNitrogen600};
    }
  }
`;

const switchLabel = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;
const tooltipTrigger = css`
  display: grid;
  place-items: center;
`;
interface P extends React.InputHTMLAttributes<HTMLInputElement> {
  position?: "left" | "right";
  spread?: boolean;
  loading?: boolean;
  sizeType?: "normal" | "small";
  tooltipOverlay?: React.ReactNode;
  justify?: boolean;
}

const Switch = (
  {
    position = "left",
    loading = false,
    disabled,
    onClick,
    className,
    children,
    hidden,
    sizeType = "normal",
    tooltipOverlay,
    justify,
    ...props
  }: P,
  forwardedRef: React.Ref<HTMLInputElement>,
) => {
  const inputRef = useEnsuredForwardedRef(forwardedRef);
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!disabled && e.keyCode === 32 /* spacebar */) inputRef.current?.click();
    },
    [disabled, inputRef],
  );
  const isLeft = position === "left";
  const isRight = position === "right";
  const isSmall = sizeType === "small";

  return hidden ? null : (
    <Wrapper
      className={cn(className, {
        [LOADING]: loading,
        [DISABLED]: disabled,
        [SMALL]: isSmall,
        justify,
      })}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
    >
      {isRight ? children : null}
      <HiddenInput
        ref={inputRef}
        type="checkbox"
        disabled={disabled || loading}
        onClick={loading ? undefined : onClick}
        {...props}
      />
      <SwitchRail
        role="button"
        className={cn({
          [LEFT]: isLeft,
          [RIGHT]: isRight,
          [LOADING]: loading,
          [SMALL]: isSmall,
        })}
      >
        <SwitchWagon className={cn({ [LOADING]: loading, [SMALL]: isSmall })}>
          {loading ? <SpinnerIcon className={cn({ [SMALL]: isSmall })} /> : null}
        </SwitchWagon>
      </SwitchRail>
      <span className={switchLabel}>
        {isLeft ? children : null}
        {tooltipOverlay && (
          <TooltipV2
            overlay={<span style={{ maxWidth: "350px", display: "block" }}>{tooltipOverlay}</span>}
          >
            <span className={tooltipTrigger}>
              <InformationLine />
            </span>
          </TooltipV2>
        )}
      </span>
    </Wrapper>
  );
};

export default React.memo(React.forwardRef<HTMLInputElement, P>(Switch));
